import { allocationsRestricted } from '@ck';
import type { Color, ColorMode } from '@color';
import { AdaptiveColor, ColorName, getColor } from '@color';
import type { Angle } from '@geom';
import { LineSegment, Matrix, Path, path, Point, point, rect } from '@geom';
import { LRUCache } from 'lru-cache';
import { PathOp } from './PathOp';
import { Shape, ShapeBuilder } from './Shape';
import { PaintStyle, StrokeCap, withVisualPaint, type Visual } from './Visual';

export enum ArrowType {
  TRIANGLE = 'TRIANGLE',
  OPEN_TRIANGLE = 'OPEN_TRIANGLE',
  SQUARE = 'SQUARE',
  OPEN_SQUARE = 'OPEN_SQUARE',
  CIRCLE = 'CIRCLE',
  OPEN_CIRCLE = 'OPEN_CIRCLE',
  DIAMOND = 'DIAMOND',
  OPEN_DIAMOND = 'OPEN_DIAMOND'
}

export function drawArrow(
  visual: Visual,
  line: LineSegment,
  color: Color,
  width: number,
  opts?: ArrowOpts
) {
  const path = Path.fromLineSegment(line);
  drawArrowPathByPieces(visual, path, color, width, opts);
}

export function drawArrowPathByPieces(
  visual: Visual,
  path: Path,
  color: Color,
  width: number,
  opts?: ArrowOpts & { headColor?: Color; tailColor?: Color }
) {
  const { headType, headColor, tailType, tailColor } = opts ?? {};

  let startT = 0;
  let stopT = 1;

  let headShape: Shape | undefined;
  let remainingPathLength = path.length;
  if (headType !== undefined) {
    const { shape, headLength } = getArrowheadShape(headType, width);
    headShape = shape;
    remainingPathLength -= headLength;
    stopT = 1 - headLength / path.length;
  }

  let tailShape: Shape | undefined;
  if (tailType !== undefined) {
    const { shape, headLength } = getArrowheadShape(tailType, width);
    tailShape = shape;
    remainingPathLength -= headLength;
    startT = headLength / path.length;
  }

  const drawBody = remainingPathLength > 0;

  let bodyShape = Shape.fromPath(path);
  if (drawBody && (startT > 0 || stopT < 1)) {
    const a = bodyShape.trim(startT, stopT, false);
    bodyShape.delete();
    bodyShape = a;
  }

  withVisualPaint(paint => {
    paint.style = PaintStyle.FILL;

    if (headShape !== undefined) {
      const end = path.end;
      let endAngle: Angle;
      if (drawBody) {
        endAngle = point(bodyShape.getPoint(bodyShape.countPoints() - 1)).angleTo(end);
      } else {
        endAngle = path.start.angleTo(end);
      }
      const translate = Matrix.translation(end);
      const rotate = Matrix.rotation(endAngle);
      const transform = translate.product(rotate);

      paint.color = headColor ?? color;

      visual.save();
      visual.concat(transform);
      visual.drawShape(headShape, paint);
      visual.restore();
    }

    if (tailShape !== undefined) {
      const start = path.start;
      let startAngle: Angle;
      if (drawBody) {
        startAngle = point(bodyShape.getPoint(0)).angleTo(start);
      } else {
        startAngle = path.end.angleTo(start);
      }
      const translate = Matrix.translation(start);
      const rotate = Matrix.rotation(startAngle);
      const transform = translate.product(rotate);

      paint.color = tailColor ?? color;

      visual.save();
      visual.concat(transform);
      visual.drawShape(tailShape, paint);
      visual.restore();
    }

    if (drawBody) {
      paint.style = PaintStyle.STROKE;
      paint.color = color;
      paint.strokeCap = StrokeCap.ROUND;
      paint.strokeWidth = width;
      visual.drawShape(bodyShape, paint);
    }
  });

  bodyShape.delete();
}

export function makeArrowShape(path: Path, width: number, opts: ArrowOpts): Shape {
  const { headType, tailType } = opts;

  let startT = 0;
  let stopT = 1;

  let headShape: Shape | undefined;
  let remainingPathLength = path.length;
  if (headType !== undefined) {
    const { shape, headLength } = getArrowheadShape(headType, width);
    headShape = shape.copy();
    remainingPathLength -= headLength;
    stopT = 1 - headLength / path.length;
  }

  let tailShape: Shape | undefined;
  if (tailType !== undefined) {
    const { shape, headLength } = getArrowheadShape(tailType, width);
    tailShape = shape.copy();
    remainingPathLength = path.length;
    startT = headLength / path.length;
  }

  const drawBody = remainingPathLength > 0;

  let bodyShape = Shape.fromPath(path);
  if (drawBody && (startT > 0 || stopT < 1)) {
    const a = bodyShape.trim(startT, stopT, false);
    bodyShape.delete();
    bodyShape = a;
  }
  let strokedBodyShape: Shape;
  if (drawBody) {
    strokedBodyShape = bodyShape.stroke({ width, cap: StrokeCap.ROUND });
  } else {
    strokedBodyShape = Shape.EMPTY.copy();
  }

  if (headShape !== undefined) {
    const end = path.end;
    let endAngle: Angle;
    if (drawBody) {
      endAngle = bodyShape.getPoint(bodyShape.countPoints() - 1).angleTo(end);
    } else {
      endAngle = path.start.angleTo(end);
    }

    const translate = Matrix.translation(end);
    const rotate = Matrix.rotation(endAngle);
    const transform = translate.product(rotate);
    const transformedHeadShape = headShape.transform(transform);
    headShape.delete();

    const bodyUnionHead = strokedBodyShape.op(transformedHeadShape, PathOp.UNION);
    strokedBodyShape.delete();
    transformedHeadShape.delete();

    strokedBodyShape = bodyUnionHead;
  }

  if (tailShape !== undefined) {
    const start = path.start;
    let startAngle: Angle;
    if (drawBody) {
      startAngle = point(bodyShape.getPoint(0)).angleTo(start);
    } else {
      startAngle = path.end.angleTo(start);
    }
    const translate = Matrix.translation(start);
    const rotate = Matrix.rotation(startAngle);
    const transform = translate.product(rotate);
    const transformedTailShape = tailShape.transform(transform);
    tailShape.delete();

    const bodyUnionTail = strokedBodyShape.op(transformedTailShape, PathOp.UNION);
    strokedBodyShape.delete();
    transformedTailShape.delete();

    strokedBodyShape = bodyUnionTail;
  }

  bodyShape.delete();
  return strokedBodyShape;
}

export interface ArrowOpts {
  headType?: ArrowType;
  tailType?: ArrowType;
}

interface CacheKey {
  pathId: string;
  width: number;
  expand: number;
  opts: ArrowOpts;
}

function keyString(key: CacheKey): string {
  return `${key.pathId}:${key.width.toString()}:${key.expand.toString()}:${key.opts.headType ?? 'undefined'}:${key.opts.tailType ?? 'undefined'}`;
}

const cache = new LRUCache<string, Shape>({
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  max: allocationsRestricted ? 10 : 500,
  dispose: shape => {
    // console.log(`🗑️ Disposing ${key} because ${reason}`);
    shape.delete();
  }
});

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (allocationsRestricted) {
  console.warn('Arrow shape cache is restricted.');
}

function getArrowShape(
  path: Path,
  width: number,
  expand: number,
  opts: ArrowOpts
): Shape {
  const key = keyString({ pathId: path.id, width, expand, opts: opts });
  const shape = cache.get(key);
  if (shape !== undefined) {
    // console.log(`🟢 ${key} Cache hit`);
    return shape;
  }
  // console.log(`🔴 ${key} Cache miss`);

  if (expand > 0) {
    const arrowKey = keyString({
      pathId: path.id,
      width,
      expand: 0,
      opts: opts
    });
    let arrowPath = cache.get(arrowKey);
    if (arrowPath === undefined) {
      arrowPath = makeArrowShape(path, width, opts);
      cache.set(arrowKey, arrowPath);
    }
    const expandedShape = arrowPath.grow(expand);
    cache.set(key, expandedShape);
    return expandedShape;
  } else {
    const shape = makeArrowShape(path, width, opts);
    cache.set(key, shape);
    return shape;
  }
}

export function drawArrowPath(
  visual: Visual,
  path: Path,
  color: Color,
  width: number,
  expand: number,
  opts: ArrowOpts
) {
  withVisualPaint(paint => {
    const shape = getArrowShape(path, width, expand, opts);
    paint.style = PaintStyle.FILL;
    paint.color = color;
    visual.drawShape(shape, paint);
  });
}

const arrowheadShapeCache = new Map<string, { shape: Shape; headLength: number }>();

function getArrowheadShape(
  type: ArrowType,
  width: number
): { shape: Shape; headLength: number } {
  const key = `${type}:${width.toString()}`;
  let entry = arrowheadShapeCache.get(key);
  if (entry === undefined) {
    // console.log(`🔴 Arrowhead cache miss ${type} ${width}`);
    switch (type) {
      case ArrowType.TRIANGLE:
        entry = makeTriangle(width);
        break;
      case ArrowType.OPEN_TRIANGLE:
        entry = makeOpenTriangle(width);
        break;
      case ArrowType.SQUARE:
        entry = makeSquare(width);
        break;
      case ArrowType.OPEN_SQUARE:
        entry = makeOpenSquare(width);
        break;
      case ArrowType.CIRCLE:
        entry = makeCircle(width);
        break;
      case ArrowType.OPEN_CIRCLE:
        entry = makeOpenCircle(width);
        break;
      case ArrowType.DIAMOND:
        entry = makeDiamond(width);
        break;
      case ArrowType.OPEN_DIAMOND:
        entry = makeOpenDiamond(width);
        break;
    }
    arrowheadShapeCache.set(key, entry);
  } else {
    // console.log(`🟢 Arrowhead cache hit ${type} ${width}`);
  }
  return entry;
}

export function drawArrowDemo(visual: Visual, p: Point, mode: ColorMode) {
  visual.save();
  visual.translate(p);
  visual.scale(10);

  const width = 1;

  drawArrowDemo1(visual, width, mode);
  drawArrowDemo2(visual, width, mode);

  visual.restore();
}

function drawArrowDemo2(visual: Visual, width: number, mode: ColorMode) {
  const path = Path.fromSvgPathData(
    'M60 -60C50 -50 30 -70 20 -60L40 -40C10 -30 50 -10 20 0'
  );
  drawArrowPathByPieces(visual, path, getColor(ColorName.edge, mode), width, {
    headType: ArrowType.OPEN_DIAMOND,
    // headColor: AdaptiveColor.RED.realize(colorMode),
    tailType: ArrowType.OPEN_TRIANGLE
    // tailColor: AdaptiveColor.BLUE.realize(colorMode),
  });
}

function drawArrowDemo1(visual: Visual, width: number, mode: ColorMode) {
  withVisualPaint(paint => {
    paint.style = PaintStyle.FILL;
    paint.color = AdaptiveColor.FOREGROUND.realize(mode);

    const pth = path()
      .moveTo(point(-20 * width, 0))
      .lineTo(Point.ZERO)
      .build();

    function drawArrow(type?: ArrowType) {
      drawArrowPathByPieces(visual, pth, getColor(ColorName.edge, mode), width, {
        headType: type,
        // headColor: AdaptiveColor.RED.realize(colorMode),
        tailType: type
        // tailColor: AdaptiveColor.BLUE.realize(colorMode),
      });
    }

    drawArrow();
    visual.translate(point(0, 10 * width));
    drawArrow(ArrowType.TRIANGLE);
    visual.translate(point(0, 10 * width));
    drawArrow(ArrowType.OPEN_TRIANGLE);
    visual.translate(point(0, 10 * width));
    drawArrow(ArrowType.SQUARE);
    visual.translate(point(0, 10 * width));
    drawArrow(ArrowType.OPEN_SQUARE);
    visual.translate(point(0, 10 * width));
    drawArrow(ArrowType.CIRCLE);
    visual.translate(point(0, 10 * width));
    drawArrow(ArrowType.OPEN_CIRCLE);
    visual.translate(point(0, 10 * width));
    drawArrow(ArrowType.DIAMOND);
    visual.translate(point(0, 10 * width));
    drawArrow(ArrowType.OPEN_DIAMOND);
  });
}

function makeTriangle(width: number): { shape: Shape; headLength: number } {
  const headLength = 7.5 * width;
  const headWidth = 5 * width;
  const halfHeadWidth = headWidth / 2;
  const shape = new ShapeBuilder()
    .moveTo(point(-headLength, halfHeadWidth))
    .lineTo(point(-headLength, -halfHeadWidth))
    .lineTo(Point.ZERO)
    .close()
    .build();
  return { shape, headLength };
}

function makeOpenTriangle(width: number): { shape: Shape; headLength: number } {
  const { shape, headLength } = makeTriangle(width);
  const inlinedShape = shape.inline(width);
  shape.delete();
  return { shape: inlinedShape, headLength };
}

function makeSquare(width: number): { shape: Shape; headLength: number } {
  const headLength = 5 * width;
  const halfHeadLength = headLength / 2;
  const shape = new ShapeBuilder()
    .addRect(rect(-headLength, -halfHeadLength, 0, halfHeadLength))
    .build();
  return { shape, headLength };
}

function makeOpenSquare(width: number): { shape: Shape; headLength: number } {
  const headLength = 5 * width;
  const halfHeadLength = headLength / 2;
  const shape = new ShapeBuilder()
    .addRect(rect(-headLength, -halfHeadLength, 0, halfHeadLength))
    .addRect(
      rect(-headLength + width, -halfHeadLength + width, -width, halfHeadLength - width),
      true
    )
    .build();
  return { shape, headLength };
}

function makeCircle(width: number): { shape: Shape; headLength: number } {
  const headLength = 5 * width;
  const halfHeadLength = headLength / 2;
  const shape = new ShapeBuilder()
    .addOval(rect(-headLength, -halfHeadLength, 0, halfHeadLength))
    .build();
  return { shape, headLength };
}

function makeOpenCircle(width: number): { shape: Shape; headLength: number } {
  const headLength = 5 * width;
  const halfHeadLength = headLength / 2;
  const shape = new ShapeBuilder()
    .addOval(rect(-headLength, -halfHeadLength, 0, halfHeadLength))
    .addOval(
      rect(-headLength + width, -halfHeadLength + width, -width, halfHeadLength - width),
      true
    )
    .build();
  return { shape, headLength };
}

function makeDiamond(width: number): { shape: Shape; headLength: number } {
  const headLength = 7.5 * width;
  const headWidth = 5 * width;
  const halfHeadLength = headLength / 2;
  const halfHeadWidth = headWidth / 2;
  const shape = new ShapeBuilder()
    .moveTo(point(-headLength, 0))
    .lineTo(point(-halfHeadLength, halfHeadWidth))
    .lineTo(Point.ZERO)
    .lineTo(point(-halfHeadLength, -halfHeadWidth))
    .close()
    .build();
  return { shape, headLength: headLength - width / 2 };
}

function makeOpenDiamond(width: number): { shape: Shape; headLength: number } {
  const { shape, headLength } = makeDiamond(width);
  const inlinedShape = shape.inline(width);
  shape.delete();
  return { shape: inlinedShape, headLength };
}
